import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas } from "@/lib/seo";

const SITE = "https://shopify-freelancer.net";
const PATH = "/ratgeber/shopify-impressum";
const TITLE = "Shopify Impressum einrichten: Seite, Menü und Checkout";
const DESCRIPTION =
  "Shopify Impressum anlegen und überall verlinken: eigene Seite erstellen, Footer-Menü ergänzen, im Checkout sichtbar machen und typische Fehler vermeiden.";
const UPDATED = "2026-09-12";

const ANSWER =
  "In Shopify legen Sie das Impressum als eigene Seite unter Onlineshop und Seiten an, verlinken es im Footer-Menü und stellen sicher, dass es von jeder Seite aus mit maximal zwei Klicks erreichbar ist. Die Inhalte selbst gehören in die Hand eines Anbieters für Rechtstexte oder einer Kanzlei, technisch ist die Einrichtung in wenigen Minuten erledigt.";

const faqs = [
  {
    q: "Wo lege ich das Impressum in Shopify an?",
    a: "Im Adminbereich unter Onlineshop, dann Seiten, eine neue Seite mit dem Titel Impressum erstellen. Shopify vergibt dafür automatisch eine eigene Adresse, die Sie in Menüs verlinken können.",
  },
  {
    q: "Wie verlinke ich es im Footer?",
    a: "Unter Onlineshop und Navigation das Footer-Menü öffnen und einen Menüpunkt hinzufügen, der auf die Impressum-Seite zeigt. Der Link erscheint dann auf allen Seiten des Shops.",
  },
  {
    q: "Ist das Impressum auch im Checkout sichtbar?",
    a: "Der Checkout nutzt eigene Einstellungen. In den Shop-Richtlinien lassen sich die rechtlichen Seiten hinterlegen, damit sie auch dort verlinkt sind.",
  },
  {
    q: "Soll das Impressum bei Google indexiert werden?",
    a: "Es muss erreichbar sein, muss aber nicht ranken. Viele Shops lassen es indexierbar, weil es Vertrauen signalisiert. Wichtig ist vor allem die leichte Auffindbarkeit für Besucher.",
  },
  {
    q: "Erstellen Sie die Rechtstexte?",
    a: "Nein. Inhalte für Impressum, Datenschutz und AGB kommen von einem spezialisierten Anbieter oder einer Kanzlei. Ich baue die Seiten ein, verlinke sie korrekt und sorge dafür, dass Aktualisierungen einfach bleiben.",
  },
];

export const Route = createFileRoute("/ratgeber/shopify-impressum")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: articleSchemas({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      faqs,
      section: "Shopify Einrichtung",
      updated: UPDATED,
    }),
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Ratgeber · Einrichtung"
      title="Shopify Impressum: anlegen, verlinken, erreichbar halten"
      intro="Technisch ist das Impressum in Shopify schnell eingerichtet. Die Fehler passieren danach: Der Link fehlt im Checkout, steht nur auf der Startseite oder verschwindet nach einem Theme-Wechsel."
      source="ratgeber-shopify-impressum"
      answer={ANSWER}
      updated={UPDATED}
      sections={[
        {
          h: "Seite anlegen",
          p: [
            "Im Adminbereich unter Onlineshop und Seiten erstellen Sie eine neue Seite mit dem Titel Impressum. Die Inhalte fügen Sie aus Ihrer Quelle für Rechtstexte ein, ohne sie gestalterisch zu verändern.",
            "Achten Sie darauf, dass die Adresse der Seite sprechend bleibt und später nicht ohne Weiterleitung geändert wird.",
          ],
        },
        {
          h: "Überall verlinken",
          p: [
            "Ein Impressum, das niemand findet, erfüllt seinen Zweck nicht. Prüfen Sie die Verlinkung an allen Stellen, an denen Besucher sie erwarten.",
          ],
          list: [
            "Footer-Menü, damit der Link auf jeder Seite erscheint",
            "Shop-Richtlinien, damit der Checkout die Seiten kennt",
            "Mobile Ansicht, in der Footer-Bereiche häufig eingeklappt sind",
            "Nach jedem Theme-Wechsel erneut kontrollieren",
          ],
        },
        {
          h: "Typische Fehler",
          p: [
            "Am häufigsten sehe ich drei Dinge: Der Link existiert nur auf der Startseite, das Footer-Menü wurde nach einem Theme-Wechsel nicht neu zugewiesen, oder die Rechtstexte liegen als Bild statt als Text auf der Seite.",
            "Ebenfalls verbreitet ist eine geänderte Adresse ohne Weiterleitung, sodass alte Links ins Leere laufen.",
          ],
        },
        {
          h: "Zuständigkeit klar trennen",
          p: [
            "Die inhaltliche Verantwortung für Rechtstexte liegt bei einem spezialisierten Anbieter oder Ihrer Kanzlei. Was ich übernehme, ist die saubere technische Einbindung, die Verlinkung an allen Stellen und eine Struktur, in der Aktualisierungen ohne Aufwand möglich sind.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/ratgeber/shopify-datenschutzerklaerung", label: "Datenschutzerklärung einrichten" },
        { to: "/blog/shopify-rechtssicher-deutschland", label: "Shop rechtssicher betreiben" },
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
        { to: "/shopify-support", label: "Shopify Support" },
        { to: "/kontakt", label: "Einrichtung anfragen" },
      ]}
    />
  );
}
